<template>
  <div class="content">
    <h1>
      <b-breadcrumb>
        <b-breadcrumb-item
          v-if="isLogged()"
          :to="`/offers?filters=${filters}`"
        >
          Offers
        </b-breadcrumb-item>
        <b-breadcrumb-item active>
          <span v-if="candle">
            {{ candle.glass_text }} - {{ candle.candle_name }}
          </span>
          <span v-else-if="loadingCount > 0">
            Loading...
          </span>
          <span v-else-if="!candle">
            No candle found
          </span>
        </b-breadcrumb-item>
      </b-breadcrumb>
    </h1>
    <div v-if="candle" class="flex flex-col gap-4">
      <div v-if="isLogged()" class="flex gap-2 justify-end">
        <b-btn
          variant="white"
          size="sm"
          @click="openLabels"
        >
          Print label
        </b-btn>

        <b-btn
          variant="white"
          size="sm"
          @click="openRecipe"
        >
          Print Recipe
        </b-btn>

        <b-btn
          variant="white"
          size="sm"
          @click="openSds"
        >
          SDS
        </b-btn>

        <b-btn
          variant="white"
          size="sm"
          @click="toggleFavorite"
        >
          <div class="flex items-center gap-2">
            <icon-star :checked="candle.is_favorite" />
          </div>
        </b-btn>

        <b-dropdown
          text="Edit"
          size="sm"
          variant="primary"
          right
        >
          <b-dropdown-item @click="duplicateCandle" class="text-right">
            Duplicate
          </b-dropdown-item>
          <b-dropdown-item @click="openEditDetails" class="text-right">
            Edit details
          </b-dropdown-item>
        </b-dropdown>
      </div>
      <div class="flex flex-col lg:flex-row justify-between gap-2 lg:gap-16 lg:whitespace-nowrap">
        <div class="flex flex-col gap-2">
          <div v-if="candle.product_code" class="flex flex-col gap-2">
            <h2 class="text-lg font-bold">Photo</h2>
            <div class="w-48 bg-gray-200">
              <img
                :src="`https://vda.nepsx.cz/motifpics/${candle.product_code}.jpg`"
                alt="Product image"
                class="mix-blend-multiply"
              >
            </div>
          </div>

          <h2 class="text-lg font-bold">Details</h2>
          <candle-info :candle="candle" />

          <h2 class="text-lg font-bold">Note</h2>
          <candle-note :id="candle.id" />
        </div>

        <div v-if="candle" class="grow w-full">

          <div class="flex gap-2 items-center justify-between">
            <div class="flex gap-2 items-center">
              <h2 class="text-lg font-bold">Offers</h2>
              <div>
                <div v-if="savingCount > 0" class="flex gap-2 items-center">
                  <b-spinner label="Spinning" small></b-spinner>
                  <span>Saving..</span>
                </div>
                <div v-else>
                  Changes are saved automatically.
                </div>
              </div>
            </div>

            <div>
              <button-select
                v-model="option.market_filled"
                :options="options.market_filled"
                size="sm"
              />
            </div>
          </div>

          <table class="table table-google table-highlight mt-2">
            <thead>
              <tr>
                <th>Market</th>
                <th>Link</th>
                <th class="text-center">Price</th>
                <th class="text-center">Delivery</th>
                <th class="text-center">VAT</th>
                <th class="text-center">Sold</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="offer in filteredOffers" :key="offer.market_id">
                <td class="row-fit text-nowrap">
                  <a
                    v-if="offer.market_link"
                    class="flex gap-2 items-center text-blue"
                    :href="offer.market_link"
                    target="_blank"
                  >
                    {{ offer.market_name }}
                  </a>
                  <div v-else class="flex gap-2 items-center">
                    {{ offer.market_name }}
                  </div>
                </td>
                <td>
                  <b-form-input
                    v-model="offer.market_link"
                    size="sm"
                    autocomplete="off"
                  ></b-form-input>
                </td>
                <td class="row-fit">
                  <div class="flex gap-2 items-center">
                    <b-form-input
                      v-model="offer.market_price "
                      size="sm"
                      autocomplete="off"
                      class="w-28 text-right"
                    ></b-form-input>
                    <div class="form-control form-control-sm">
                      {{ offer.market_currency }}
                    </div>
                  </div>
                </td>
                <td class="row-fit text-right">
                  <div class="form-control form-control-sm">
                    {{ offer.market_delivery }}
                  </div>
                </td>
                <td class="row-fit text-right">
                  <div class="form-control form-control-sm">
                    {{ offer.market_vat }} %
                  </div>
                </td>
                <td class="row-fit">
                  <b-form-input
                    v-model="offer.market_sold "
                    size="sm"
                    autocomplete="off"
                    class="w-28 text-right"
                  ></b-form-input>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <h2 class="text-lg font-bold">
          Files
        </h2>

        <candle-file-groups :id="candle.id" :file-sources="fileSources" />
      </div>
    </div>

    <div v-if="candle === null && loadingCount === 0">
      This candle is no longer available.
    </div>

    <label-modal
      v-if="candle"
      :data="labelModal"
      :id="candle.id"
    />

    <create-modal
      :data="createModal"
      @created="onDuplicated"
    />

    <edit-details-modal
      v-if="candle"
      :data="modals.editDetails"
      :id="candle.id"
      @updated="candleUpdated"
    />

    <recipe-modal
      v-if="recipeModal.showModal"
      :data="recipeModal"
    />

    <sds-modal
      v-if="candle"
      :data="sdsModal"
      :id="candle.id"
      @updated="candleUpdated"
    />
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import {
  roundNumber,
  stringToNumber,
} from '@/helpers';

const ButtonSelect = () => import('@/components/global/ButtonSelect.vue');
const IconStar = () => import('@/components/global/IconStar.vue');
const CandleFileGroups = () => import('@/components/candle/CandleFileGroups.vue');
const CandleInfo = () => import('@/components/candle/CandleInfo.vue');
const CandleNote = () => import('@/components/candle/CandleNote.vue');
const EditDetailsModal = () => import('@/components/candle/EditDetailsModal.vue');
const CreateModal = () => import('@/components/candle/CreateModal.vue');
const LabelModal = () => import('@/components/candle/LabelModal.vue');
const SdsModal = () => import('@/components/candle/SdsModal.vue');
const RecipeModal = () => import('@/components/candles/RecipeModal.vue');

export default {
  name: 'Candle',
  components: {
    ButtonSelect,
    IconStar,
    CandleFileGroups,
    CandleInfo,
    CandleNote,
    EditDetailsModal,
    CreateModal,
    LabelModal,
    SdsModal,
    RecipeModal,
  },
  computed: {
    dropdowns() {
      return this.$store.state.app.dropdowns;
    },
    candleId() {
      return this.$route.params.id;
    },
    postData() {
      return this.candle.offers.map((offer) => ({
        id: offer.id,
        market_id: offer.market_id,
        market_link: offer.market_link,
        market_code: offer.market_code,
        market_sold: parseInt(stringToNumber(offer.market_sold), 10),
        market_price: stringToNumber(offer.market_price),
        currency_id: offer.currency_id,
        vat_id: offer.vat_id,
      }));
    },
    filteredOffers() {
      if (!this.candle) {
        return [];
      }
      if (this.option.market_filled === '1') {
        return this.candle.offers.filter((offer) => this.isOfferFilled(offer));
      }
      return this.candle.offers;
    },
  },
  data() {
    return {
      loadingCount: 0,
      savingCount: 0,
      option: {
        market_filled: '',
      },
      options: {
        market_filled: [
          { text: 'All', value: '' },
          { text: 'Filled', value: '1' },
        ],
      },
      candle: null,
      fileSources: [],
      modals: {
        editDetails: {
          showModal: false,
        },
      },
      createModal: {
        showModal: false,
        source: null,
      },
      labelModal: {
        showModal: false,
      },
      recipeModal: {
        showModal: false,
        candleIds: [],
        candleCount: 1,
      },
      sdsModal: {
        showModal: false,
      },
      scheduleSaving: _.debounce(() => {
        this.saveOffer();
      }, 250),
    };
  },
  methods: {
    isOfferFilled(offer) {
      return offer.market_link.length > 0;
    },
    consumeFilters() {
      this.filters = this.$route.query.filters ?? '';
      if (this.filters.length > 0) {
        const query = { ...this.$route.query };
        delete query.filters;
        this.$router.replace({ query });
      }
    },
    fetchOffer() {
      this.loadingCount++;
      this.$http
        .get(`/offers/${this.candleId}`)
        .then((res) => {
          res.body.offers.forEach((offer) => {
            offer.market_price = offer.market_price ? roundNumber(offer.market_price, 2, 2) : '';
          });
          const isFilled = res.body.offers.some((offer) => this.isOfferFilled(offer));
          if (isFilled) {
            this.option.market_filled = '1';
          }
          this.candle = res.body;
        })
        .catch((err) => {
          console.error(err);
          // this.$toast.error(`Failed to fetch candle: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    fetchFileSources() {
      this.loadingCount++;
      this.$http
        .get(`/candle/${this.candleId}/file_sources`)
        .then((res) => {
          this.fileSources = res.body;
        })
        .catch((err) => {
          this.$toast.error(`Failed to fetch file sources: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    saveOffer() {
      this.savingCount++;
      this.$http
        .post(`/offers/${this.candleId}`)
        .send({ offers: this.postData })
        .then(() => {
          // no-op
        })
        .catch((err) => {
          this.$toast.error(`Failed to update offers: ${err.response.text}`);
        })
        .finally(() => {
          this.savingCount--;
        });
    },
    toggleFavorite() {
      const changes = {
        is_favorite: !this.candle.is_favorite,
      };
      this.loadingCount++;
      this.$http
        .put(`/candle/${this.candleId}`)
        .send({ changes })
        .then(() => {
          this.candle.is_favorite = !this.candle.is_favorite;
        })
        .catch((err) => {
          this.$toast.error(`Failed to toggle favorite: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    onDuplicated(candle) {
      this.$router.push({
        name: 'Candle',
        params: { id: candle.id },
        query: {
          filters: this.filters,
        },
      }, () => {
        this.candle = candle;
      });
    },
    duplicateCandle() {
      const source = JSON.parse(JSON.stringify(this.candle));
      source.id = null;
      source.create_date = moment().format('YYYY-MM-DD');
      source.candle_reference = '';
      source.candle_note = '';
      source.candle_status = 'testing';
      source.is_favorite = false;
      source.fragrance.forEach((fragrance) => {
        fragrance.id = null;
      });
      source.dye.forEach((dye) => {
        dye.id = null;
      });
      source.additive.forEach((additive) => {
        additive.id = null;
      });
      this.createModal.source = source;
      this.createModal.showModal = true;
    },
    openEditDetails() {
      this.modals.editDetails.showModal = true;
    },
    candleUpdated(candle) {
      this.candle = {
        ...candle,
      };
    },
    openRecipe() {
      this.recipeModal.showModal = true;
      this.recipeModal.candleIds = [this.candle.id];
    },
    openSds() {
      this.sdsModal.showModal = true;
    },
    openLabels() {
      this.labelModal.showModal = true;
    },
  },
  created() {
    this.consumeFilters();
    this.fetchOffer();
    this.fetchFileSources();
  },
  watch: {
    'candle.offers': {
      deep: true,
      handler(newValue, oldValue) {
        if (oldValue === null || oldValue === undefined) {
          return;
        }
        this.scheduleSaving();
      },
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
